import React, { useMemo, useState } from "react";
import styles from "./ReportTimeline.module.css";

export interface RibbonSegment {
  state: string;
  /** Posição cumulativa (0..100) e largura (0..100) proporcional à duração visível. */
  startPct: number;
  widthPct: number;
  startedAt: string;
  seconds: number;
}

interface TFn {
  (key: string, options?: Record<string, unknown>): string;
}

export interface TimelineRibbonProps {
  segments: RibbonSegment[];
  colorOf: (state: string) => string;
  labelOf: (state: string) => string;
  fmtTime: (iso: string) => string;
  fmtDur: (s: number) => string;
  t: TFn;
}

const TimelineRibbon: React.FC<TimelineRibbonProps> = ({ segments, colorOf, labelOf, fmtTime, fmtDur, t }) => {
  const [hover, setHover] = useState<number>(-1);

  // Ticks nas fronteiras dos segmentos, desbastados p/ não sobrepor rótulos (mín. ~7% entre eles).
  const ticks = useMemo(() => {
    const out: Array<{ leftPct: number; label: string }> = [];
    let last = -100;
    segments.forEach((s) => {
      if (s.startPct >= last + 7) {
        out.push({ leftPct: s.startPct, label: fmtTime(s.startedAt) });
        last = s.startPct;
      }
    });
    return out;
  }, [segments, fmtTime]);

  const hov = hover >= 0 && hover < segments.length ? segments[hover] : null;
  const tipLeft = hov ? Math.max(6, Math.min(94, hov.startPct + hov.widthPct / 2)) : 0;

  return (
    <div className={styles.ribbonWrap}>
      <div className={styles.ribbon}>
        {segments.map((s, i) => (
          <div
            key={i}
            className={styles.ribbonSeg}
            style={{ left: `${s.startPct}%`, width: `${s.widthPct}%`, background: colorOf(s.state) }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(-1)}
            role="img"
            aria-label={`${labelOf(s.state)} · ${fmtTime(s.startedAt)} · ${fmtDur(s.seconds)}`}
          />
        ))}
      </div>
      {/* Tooltip FORA da .ribbon (que tem overflow:hidden p/ os cantos); .ribbonWrap não clipa. */}
      {hov && (
        <div className={styles.ribbonTip} style={{ left: `${tipLeft}%` }}>
          <span className={styles.ribbonTipState}>
            <span className={styles.ribbonTipDot} style={{ background: colorOf(hov.state) }} />
            {labelOf(hov.state)}
          </span>
          <span className={styles.ribbonTipRow}>{t("colStart")}: {fmtTime(hov.startedAt)}</span>
          <span className={styles.ribbonTipRow}>{t("colDur")}: {fmtDur(hov.seconds)}</span>
        </div>
      )}
      <div className={styles.axis}>
        {ticks.map((tk, i) => (
          <span key={i} className={styles.axisTick} style={{ left: `${tk.leftPct}%` }}>
            {tk.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default TimelineRibbon;
